import * as React from 'react';
import { CalendarIcon } from '@phosphor-icons/react';

import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';

type FormDatePickerProps = {
  id: string;
  name?: string;
  label?: string;
  value?: string;
  placeholder?: string;
  helperText?: string;
  errorText?: string | Array<string | undefined>;
  disabled?: boolean;
  fromYear?: number;
  toYear?: number;
  className?: string;
  forceShowErrors?: boolean;
  isTouched?: boolean;
  onChange: (value: string | undefined) => void;
  onBlur?: () => void;
};

function parseDate(value?: string) {
  if (!value) return undefined;
  const [year, month, day] = value.split('-').map(Number);
  if (
    Number.isNaN(year) ||
    Number.isNaN(month) ||
    Number.isNaN(day) ||
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > 31
  ) {
    return undefined;
  }
  return new Date(year, month - 1, day);
}

function formatDate(date: Date | undefined) {
  if (!date) return undefined;
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function FormDatePicker({
  id,
  name,
  label,
  value,
  placeholder = 'Select date',
  helperText,
  errorText,
  disabled,
  fromYear = 2020,
  toYear = 2030,
  className,
  forceShowErrors = false,
  isTouched = false,
  onChange,
  onBlur,
}: FormDatePickerProps) {
  const [open, setOpen] = React.useState(false);

  const selected = React.useMemo(() => parseDate(value), [value]);
  const cleanErrors = Array.isArray(errorText)
    ? (errorText.filter(Boolean) as string[])
    : errorText
      ? [errorText]
      : [];

  const showErrors = forceShowErrors || isTouched;
  const hasErrors = cleanErrors.length > 0;

  const displayValue =
    selected?.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }) ?? placeholder;

  return (
    <div className={cn('space-y-2', className)}>
      {label ? <h2 className="text-large text-slate-50">{label}</h2> : null}

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            id={id}
            name={name}
            disabled={disabled}
            className={cn(
              'w-full h-14 justify-between border-purple-400 font-normal',
              !selected && 'text-muted-foreground',
              showErrors && hasErrors && 'border-red-500'
            )}
            aria-invalid={showErrors && hasErrors}
            onBlur={onBlur}
          >
            {displayValue}
            <CalendarIcon className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={selected}
            captionLayout="dropdown"
            fromYear={fromYear}
            toYear={toYear}
            onSelect={(date) => {
              const formatted = formatDate(date ?? undefined);
              onChange(formatted);
              setOpen(false);
            }}
          />
        </PopoverContent>
      </Popover>

      {helperText ? (
        <p className="text-xs text-muted-foreground">{helperText}</p>
      ) : null}
      {showErrors && hasErrors ? (
        <p className="text-sm text-red-500">{cleanErrors.join(', ')}</p>
      ) : null}
    </div>
  );
}

